/**
 * Background GPS Tracker — ShiftQuote Auto-Track.
 *
 * Manages the @capacitor-community/background-geolocation plugin lifecycle.
 * Integrates location permissions, trip detection, and notifications.
 *
 * ─── Trip Detection Parameters ───────────────────────────────────────────
 * These are tuned for rideshare/delivery drivers to avoid stop-and-go spam:
 *
 *   TRIP START:  30 seconds continuous movement + speed > 5 mph
 *   TRIP END:    2 minutes fully stationary (red lights, drive-throughs don't end it)
 *   MIN DISTANCE: 0.5 miles (parking lot shuffles are discarded)
 *   COOLDOWN:    2 minutes after a trip ends before a new trip can start
 *
 * ─── Battery Optimization ────────────────────────────────────────────────
 *   Two-phase GPS accuracy:
 *     IDLE MODE:   enableHighAccuracy=false, distanceFilter=100m (low power)
 *     ACTIVE MODE: enableHighAccuracy=true, distanceFilter=50m (trip recording)
 *   5-second minimum update interval prevents GPS flooding at speed.
 *
 * ─────────────────────────────────────────────────────────────────────────
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import { notifyTripStarted, notifyTripEnded } from "./tripNotifications";
import { api } from "./api";

// ─── Configuration Constants ─────────────────────────────────────────────
const TRIP_START_THRESHOLD_SECONDS = 30;   // Must move 30s continuously
const TRIP_START_MIN_SPEED_MPH = 5;        // Must exceed 5 mph
const TRIP_END_STATIONARY_MS = 2 * 60 * 1000; // 2 minutes stationary = trip over
const MIN_TRIP_DISTANCE_MILES = 0.5;       // Discard trips shorter than 0.5 mi
const COOLDOWN_MS = 2 * 60 * 1000;         // 2 min cooldown after trip ends
const MAX_VALID_SPEED_MPH = 100;           // Trips avg > 100 mph = noise (flight)
const DISTANCE_FILTER_METERS = 50;         // GPS update every 50m of movement (active trip)
const IDLE_DISTANCE_FILTER_METERS = 100;   // GPS update every 100m in idle (low power)
const MIN_UPDATE_INTERVAL_MS = 5000;       // 5-second debounce between GPS updates

// ─── Native Plugin Registration ──────────────────────────────────────────
// @capacitor-community/background-geolocation is a native-only plugin with no
// JavaScript implementation — it exposes only type definitions and native code.
// We use registerPlugin() which is the correct Capacitor pattern: on native
// platforms the bridge resolves the plugin; on web it returns a proxy that
// throws if methods are called (but we guard with isNativePlatform() first).
const BackgroundGeolocation = registerPlugin("BackgroundGeolocation");

// ─── Internal State ──────────────────────────────────────────────────────
let _watcher = null;
let _tripBuffer = [];          // GPS points for current trip
let _tripActive = false;       // True once trip-start notification has fired
let _movingStartTime = null;   // When continuous movement started
let _lastMovingTime = null;    // Last timestamp with speed > threshold
let _lastTripEndTime = null;   // When last trip ended (for cooldown)
let _stationaryTimer = null;   // Timer that fires trip-end after 2 min
let _startNotified = false;    // Prevent duplicate start notifications
let _lastUpdateTime = null;    // Timestamp of last processed GPS update (debounce)
let _watcherMode = null;       // Current watcher mode: "idle" or "active"

// ─── App foreground/background state ───────────────────────────────────
// Mode switches (idle <-> active) used to remove the only native watcher and
// add a new one. On Android, removing the last watcher calls stopForeground()
// and adding one calls startForeground() again. Mid-drive the app is usually
// in the background, and Android 12+ blocks/re-evaluates foreground-service
// starts from the background (Android 14+: a location FGS started from the
// background loses while-in-use location unless "Allow all the time" is
// granted). iOS can likewise refuse to start fresh location updates from the
// background without "Always" permission.
//
// So now:
//   * the existing watcher is NEVER torn down for a mode change while the app
//     is in the background: the new mode is remembered and applied the next
//     time the app comes to the foreground;
//   * in the foreground the switch is make-before-break (add the new watcher,
//     then remove the old one), so the service never drops to zero watchers
//     and the persistent notification does not flicker.
// Both plugin builds (Android 1.2.x, iOS) always request high accuracy, so
// the only real difference between the modes is distanceFilter (100 m vs 50 m),
// which the 5-second debounce already caps above ~45 mph.
let _appActive = typeof document === "undefined" || document.visibilityState !== "hidden";
let _pendingMode = null;        // Mode requested while in background, applied on resume
let _appStateListener = null;   // @capacitor/app listener handle
let _modeSwitchChain = Promise.resolve(); // Serializes native add/remove calls

function isAppInForeground() {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return false;
  return _appActive;
}

async function ensureAppStateListener() {
  if (_appStateListener) return;
  try {
    const { App } = await import("@capacitor/app");
    try {
      const st = await App.getState();
      if (st && typeof st.isActive === "boolean") _appActive = st.isActive;
    } catch (_) { /* keep visibility-based default */ }
    _appStateListener = await App.addListener("appStateChange", ({ isActive }) => {
      _appActive = !!isActive;
      if (_appActive && _pendingMode && _watcher) {
        const mode = _pendingMode;
        _pendingMode = null;
        setWatcherMode(mode).catch((e) =>
          console.warn("[backgroundTracker] Deferred mode switch failed:", e)
        );
      }
    });
  } catch (e) {
    console.warn("[backgroundTracker] App state listener unavailable:", e);
  }
}

async function removeAppStateListener() {
  const l = _appStateListener;
  _appStateListener = null;
  if (l && typeof l.remove === "function") {
    try { await l.remove(); } catch (_) { /* ignore */ }
  }
}

/**
 * Add a native watcher with the options for `mode`. Returns the watcher id.
 */
async function addNativeWatcher(mode) {
  const isActive = mode === "active";
  const distanceFilter = isActive ? DISTANCE_FILTER_METERS : IDLE_DISTANCE_FILTER_METERS;
  const enableHighAccuracy = isActive;

  let id; // assigned once addWatcher resolves; callbacks can arrive before that
  id = await BackgroundGeolocation.addWatcher(
    {
      backgroundMessage: "ShiftQuote is tracking your trip",
      backgroundTitle: "Mileage Tracker Active",
      requestPermissions: true,
      stale: false,
      distanceFilter: distanceFilter,
      enableHighAccuracy: enableHighAccuracy,
      notificationActions: [{ title: "Stop Tracking", identifier: "stop_tracking" }],
    },
    (location, error) => {
      // Ignore late callbacks from a watcher that has been replaced/stopped
      if (id !== undefined && _watcher !== null && _watcher !== id) return;
      if (error) {
        if (error.code === "NOT_AUTHORIZED") {
          console.warn("[backgroundTracker] Location permission revoked");
        }
        return;
      }
      if (location) {
        handleLocationUpdate(location);
      }
    }
  );
  console.info("[backgroundTracker] Watcher started in", mode, "mode (distanceFilter:", distanceFilter + "m, highAccuracy:", enableHighAccuracy + ")");
  return id;
}

/**
 * Switch the running tracker to `mode` ("idle" | "active") WITHOUT tearing
 * down the foreground service. No-op when the tracker is stopped (so a trip
 * finalized by stopBackgroundTracker() can no longer restart the watcher) or
 * already in that mode. Deferred while the app is in the background.
 */
function setWatcherMode(mode) {
  const run = async () => {
    if (!_watcher) return;                       // tracker stopped: never re-add
    if (_watcherMode === mode) { _pendingMode = null; return; }
    if (!isAppInForeground()) {
      _pendingMode = mode;                       // keep the existing watcher alive
      console.info("[backgroundTracker] App in background: keeping", _watcherMode, "watcher, will switch to", mode, "on resume");
      return;
    }
    const oldId = _watcher;
    const newId = await addNativeWatcher(mode);  // make...
    if (!_watcher) {
      // Tracker was stopped while we were adding: undo
      try { await BackgroundGeolocation.removeWatcher({ id: newId }); } catch (_) { /* ignore */ }
      return;
    }
    _watcher = newId;
    _watcherMode = mode;
    _pendingMode = null;
    try {
      await BackgroundGeolocation.removeWatcher({ id: oldId }); // ...before break
    } catch (e) {
      console.warn("[backgroundTracker] Failed to remove old watcher during mode switch:", e);
    }
  };
  _modeSwitchChain = _modeSwitchChain.then(run, run);
  return _modeSwitchChain;
}

/**
 * Start the background geolocation tracker.
 * Called after location permission is confirmed as "always/granted".
 */
export async function startBackgroundTracker() {
  if (!Capacitor.isNativePlatform()) {
    console.info("[backgroundTracker] Skipping — not native platform");
    return;
  }

  // Don't double-start
  if (_watcher) {
    console.info("[backgroundTracker] Already running");
    return;
  }

  try {
    // Start in idle mode (low power) — switches to active when trip starts
    _pendingMode = null;
    _watcher = await addNativeWatcher("idle");
    _watcherMode = "idle";
    ensureAppStateListener();
  } catch (e) {
    console.error("[backgroundTracker] Failed to start:", e);
    throw e;
  }
}

/**
 * Stop the background geolocation tracker.
 * Called when user disables Auto-Track.
 */
export async function stopBackgroundTracker() {
  if (_watcher) {
    const id = _watcher;
    _watcher = null;          // set first so in-flight/queued mode switches no-op
    _watcherMode = null;
    _pendingMode = null;
    try {
      await BackgroundGeolocation.removeWatcher({ id });
    } catch (e) {
      console.warn("[backgroundTracker] Failed to remove watcher:", e);
    }
  }
  removeAppStateListener();

  // Clear any pending trip
  clearStationaryTimer();
  if (_tripActive && _tripBuffer.length > 0) {
    // Save whatever we have if it meets minimum distance
    await finalizeTrip();
  }
  resetTripState();
}

/**
 * Process each GPS location update from the background plugin.
 */
function handleLocationUpdate(location) {
  const now = Date.now();

  // ─── 5-Second Debounce ───────────────────────────────────────────────
  if (_lastUpdateTime !== null && (now - _lastUpdateTime) < MIN_UPDATE_INTERVAL_MS) {
    return; // Ignore updates arriving faster than 5 seconds
  }
  _lastUpdateTime = now;

  const rawSpeed = location.speed;
  const speedMps = (rawSpeed !== null && rawSpeed !== undefined && rawSpeed >= 0) ? rawSpeed : 0; // meters/second
  const speedMph = speedMps * 2.23694;  // convert to mph
  const isMoving = speedMph >= TRIP_START_MIN_SPEED_MPH;

  const point = {
    latitude: location.latitude,
    longitude: location.longitude,
    timestamp: location.time || now,
    speed: speedMph,
    accuracy: location.accuracy,
  };

  // ─── Cooldown Check ──────────────────────────────────────────────────
  if (_lastTripEndTime && (now - _lastTripEndTime) < COOLDOWN_MS) {
    // Still in cooldown period — ignore movement
    return;
  }

  // ─── Trip Start Detection ────────────────────────────────────────────
  if (!_tripActive) {
    if (isMoving) {
      if (!_movingStartTime) {
        _movingStartTime = now;
      }
      _lastMovingTime = now;

      // Check if 30 seconds of continuous movement has passed
      const movingDuration = (now - _movingStartTime) / 1000;
      if (movingDuration >= TRIP_START_THRESHOLD_SECONDS && !_startNotified) {
        // TRIP STARTED — fire notification and begin recording
        _tripActive = true;
        _startNotified = true;
        _tripBuffer = []; // Fresh buffer
        notifyTripStarted();
        console.info("[backgroundTracker] Trip started after", movingDuration.toFixed(0), "s of movement");

        // Switch to active (high-accuracy) mode
        setWatcherMode("active").catch((e) => {
          console.warn("[backgroundTracker] Failed to switch to active mode:", e);
        });
      }
    } else {
      // Stopped moving before reaching threshold — reset
      _movingStartTime = null;
    }
  }

  // ─── Active Trip: Buffer Points ──────────────────────────────────────
  if (_tripActive) {
    _tripBuffer.push(point);

    if (isMoving) {
      _lastMovingTime = now;
      // User is moving — cancel any pending trip-end timer
      clearStationaryTimer();
    } else {
      // User stopped — start the 2-minute stationary countdown
      if (!_stationaryTimer) {
        _stationaryTimer = setTimeout(() => {
          // 2 full minutes with no movement — end the trip
          finalizeTrip();
        }, TRIP_END_STATIONARY_MS);
      }
    }
  }
}

/**
 * End the current trip and switch back to idle GPS mode.
 */
async function stopCurrentTrip() {
  await finalizeTrip();
  // Switch back to idle (low-power) mode
  try {
    await setWatcherMode("idle");
  } catch (e) {
    console.warn("[backgroundTracker] Failed to switch back to idle mode:", e);
  }
}

/**
 * Finalize the current trip: validate, submit to backend, notify.
 */
async function finalizeTrip() {
  clearStationaryTimer();

  if (_tripBuffer.length < 2) {
    resetTripState();
    return;
  }

  // Calculate total distance
  const totalMiles = haversineSum(_tripBuffer);

  // Check minimum distance
  if (totalMiles < MIN_TRIP_DISTANCE_MILES) {
    console.info("[backgroundTracker] Trip discarded — only", totalMiles.toFixed(2), "mi (< 0.5 mi minimum)");
    resetTripState();
    return;
  }

  // Check for unrealistic speed (likely in a plane)
  const durationHours =
    (_tripBuffer[_tripBuffer.length - 1].timestamp - _tripBuffer[0].timestamp) / 3600000;
  const avgSpeed = durationHours > 0 ? totalMiles / durationHours : 0;
  if (avgSpeed > MAX_VALID_SPEED_MPH) {
    console.info("[backgroundTracker] Trip discarded — avg speed", avgSpeed.toFixed(0), "mph (likely flight)");
    resetTripState();
    return;
  }

  // Build trip payload
  const tripData = {
    start_time: new Date(_tripBuffer[0].timestamp).toISOString(),
    end_time: new Date(_tripBuffer[_tripBuffer.length - 1].timestamp).toISOString(),
    start_lat: _tripBuffer[0].latitude,
    start_lng: _tripBuffer[0].longitude,
    end_lat: _tripBuffer[_tripBuffer.length - 1].latitude,
    end_lng: _tripBuffer[_tripBuffer.length - 1].longitude,
    distance_miles: Math.round(totalMiles * 100) / 100,
    auto_tracked: true,
    purpose: "business", // Default for gig drivers — can be changed later
  };

  // Submit to backend
  try {
    await api.post("/mileage/trip", tripData);
    console.info("[backgroundTracker] Trip saved:", totalMiles.toFixed(2), "mi");
    // Fire end notification
    notifyTripEnded();

    // ─── Parking Save: extract last coordinate ───────────────────────────
    const lastCoord = _tripBuffer[_tripBuffer.length - 1];
    const parkingPayload = {
      lat: lastCoord.latitude,
      lng: lastCoord.longitude,
      address: null, // Reverse geocode happens on display side
    };
    try {
      await api.post("/mileage/parking", parkingPayload);
    } catch (pe) {
      console.warn("[backgroundTracker] Parking save failed, saving to Preferences:", pe);
    }
    // Always save to Capacitor Preferences as fallback
    try {
      const { Preferences } = await import("@capacitor/preferences");
      await Preferences.set({
        key: "sq_last_parking",
        value: JSON.stringify({ ...parkingPayload, saved_at: new Date().toISOString() }),
      });
    } catch (_) { /* web fallback */ }
  } catch (e) {
    console.error("[backgroundTracker] Failed to save trip:", e);
    // TODO: Queue for retry when network is available
  }

  // Set cooldown timestamp
  _lastTripEndTime = Date.now();
  resetTripState();

  // Switch back to idle (low-power) mode after trip ends.
  // No-op if Auto-Track was just turned off (stopBackgroundTracker), so the
  // watcher/notification is not resurrected; deferred if app is in background.
  try {
    await setWatcherMode("idle");
  } catch (e) {
    console.warn("[backgroundTracker] Failed to switch back to idle mode after trip:", e);
  }
}

/**
 * Reset all trip-tracking state for a fresh detection cycle.
 */
function resetTripState() {
  _tripActive = false;
  _startNotified = false;
  _movingStartTime = null;
  _lastMovingTime = null;
  _tripBuffer = [];
  clearStationaryTimer();
  // Note: _lastUpdateTime is NOT reset — debounce persists across trips
}

/**
 * Clear the stationary timeout if active.
 */
function clearStationaryTimer() {
  if (_stationaryTimer) {
    clearTimeout(_stationaryTimer);
    _stationaryTimer = null;
  }
}

// ─── Haversine Utilities ─────────────────────────────────────────────────

/**
 * Sum haversine distances between consecutive GPS points.
 * @param {Array} points - Array of {latitude, longitude} objects
 * @returns {number} Total distance in miles
 */
function haversineSum(points) {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += haversine(points[i - 1], points[i]);
  }
  return total;
}

/**
 * Haversine distance between two GPS coordinates.
 * @returns {number} Distance in miles
 */
function haversine(a, b) {
  const R = 3958.8; // Earth's radius in miles
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);

  const x =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
}

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

/**
 * Returns true if the background tracker is currently running.
 */
export function isTracking() {
  return _watcher !== null;
}

/**
 * Returns the current trip distance in miles (0 if no active trip).
 */
export function getTrackDistance() {
  if (!_tripActive || _tripBuffer.length < 2) return 0;
  return haversineSum(_tripBuffer);
}

/**
 * Returns the trip buffer coords (for parking save on finalize).
 */
export function getTripCoords() {
  return [..._tripBuffer];
}

export { haversineSum, resetTripState };

// Test-only hooks (no effect on app behavior)
export const __test__ = {
  setWatcherMode,
  handleLocationUpdate,
  getState: () => ({ watcher: _watcher, mode: _watcherMode, pendingMode: _pendingMode, appActive: _appActive }),
  setAppActive: (v) => { _appActive = !!v; },
};
